import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
// Goes through /api/ai-gateway/manage, which the Vite dev proxy (locally) or
// nginx (deployed) authenticates. The gateway admin API itself rejects
// browser calls that carry no gateway credential.
import { gatewayAdminAPI } from "../AIGateway/api";
import { type UserNameMap } from "../../utils/userNames";
import CollapsibleSection from "../Billing/CollapsibleSection";
import SectionHeading from "./SectionHeading";

interface SpendTokens {
  prompt?: number;
  completion?: number;
  cached?: number;
  reasoning?: number;
}

interface SpendGroup {
  key: string;
  costUsd?: number;
  requests?: number;
  tokens?: SpendTokens;
  units?: { images?: number };
}

interface SpendResponse {
  group_by: string;
  groups: SpendGroup[];
  total: SpendGroup;
}

const GROUP_BY_OPTIONS = [
  { value: "model", label: "Model" },
  { value: "provider", label: "Provider" },
  { value: "kind", label: "Request kind" },
  { value: "product", label: "Product" },
  { value: "user", label: "User" },
];

const integer = new Intl.NumberFormat();
const compact = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});
const usd = (value?: number) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const headClass =
  "bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]";
const cellClass =
  "font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]";

export default function GatewayUsage({
  workspaceId,
  startDate,
  endDate,
  userNames = {},
}: {
  workspaceId: string;
  startDate?: string;
  endDate?: string;
  userNames?: UserNameMap;
}) {
  const [groupBy, setGroupBy] = useState("model");
  const [spend, setSpend] = useState<SpendResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Guards against a slow response for an old range overwriting a newer one.
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    const query = new URLSearchParams({
      group_by: groupBy,
      workspace: workspaceId,
    });
    if (startDate) query.set("from", startDate);
    if (endDate) query.set("to", endDate);

    setLoading(true);
    setError("");
    gatewayAdminAPI<SpendResponse>(`/spend?${query.toString()}`)
      .then((response) => {
        if (requestId === requestRef.current) setSpend(response);
      })
      .catch((err: Error) => {
        if (requestId !== requestRef.current) return;
        setSpend(null);
        setError(err.message || "AI Gateway usage could not be loaded.");
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [workspaceId, startDate, endDate, groupBy]);

  const total = spend?.total;
  const tokens = total?.tokens || {};
  const stats = [
    { label: "Spend (USD)", value: total ? usd(total.costUsd) : undefined },
    {
      label: "Requests",
      value: total ? integer.format(total.requests || 0) : undefined,
    },
    {
      label: "Tokens",
      value: total
        ? compact.format((tokens.prompt || 0) + (tokens.completion || 0))
        : undefined,
    },
    {
      label: "Images",
      value: total ? integer.format(total.units?.images || 0) : undefined,
    },
  ];
  const groups = spend?.groups || [];
  const groupLabel =
    GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.label ||
    groupBy;

  return (
    <section
      className="flex w-full flex-col gap-3"
      data-name="AI-Gateway usage"
    >
      <SectionHeading title="AI-Gateway usage" />

      <div
        className="grid w-full grid-cols-2 gap-3 md:grid-cols-4"
        data-name="AI-Gateway totals"
      >
        {stats.map(({ label, value }) => (
          <div
            key={label}
            className="rounded-2xl border border-[#eae7ec] bg-[rgba(253,252,253,0.88)] px-4 py-3"
          >
            <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px] uppercase">
              {label}
            </div>
            <div className="mt-1 font-['Geist:SemiBold',_sans-serif] font-semibold text-[#211f26] text-[22px] leading-[28px]">
              {loading ? "…" : (value ?? "—")}
            </div>
          </div>
        ))}
      </div>

      {error ? (
        <div className="w-full rounded-2xl border border-[#f1c9c5] bg-[#fef3f2] px-6 py-4 text-[14px] text-[#b42318]">
          {error}
        </div>
      ) : (
        <div className="w-full">
          <CollapsibleSection
            title={`Usage by ${groupLabel.toLowerCase()}`}
            subtitle={
              spend
                ? `${groups.length} ${groups.length === 1 ? "row" : "rows"}`
                : undefined
            }
            headerRight={
              <label className="flex items-center gap-2 text-[13px] text-[#5f5a66]">
                Group by
                <select
                  aria-label="Group AI-Gateway usage by"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="h-8 rounded-lg border border-[#d7d3dc] bg-white px-2 text-[13px] text-[#211f26]"
                >
                  {GROUP_BY_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            }
          >
            {groups.length === 0 ? (
              <div className="w-full rounded-2xl border border-[#eae7ec] px-6 py-10 text-center text-[14px] text-[#84828e]">
                {loading
                  ? "Loading AI-Gateway usage..."
                  : "No AI-Gateway usage was recorded in the selected date range."}
              </div>
            ) : (
              <div
                className="bg-[rgba(253,252,253,0.88)] border border-[#eae7ec] rounded-2xl w-full overflow-hidden"
                data-name="AI-Gateway usage table"
              >
                <Table className="w-full min-w-[720px]">
                  <TableHeader>
                    <TableRow className="border-b-0">
                      <TableHead className={headClass}>
                        {groupLabel.toUpperCase()}
                      </TableHead>
                      <TableHead className={`${headClass} text-right`}>
                        REQUESTS
                      </TableHead>
                      <TableHead className={`${headClass} text-right`}>
                        PROMPT TOKENS
                      </TableHead>
                      <TableHead className={`${headClass} text-right`}>
                        COMPLETION TOKENS
                      </TableHead>
                      <TableHead className={`${headClass} text-right`}>
                        CACHED TOKENS
                      </TableHead>
                      <TableHead className={`${headClass} text-right`}>
                        IMAGES
                      </TableHead>
                      <TableHead className={`${headClass} text-right`}>
                        SPEND (USD)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groups.map((group) => (
                      <TableRow
                        key={group.key}
                        className="h-[56px] border-b border-[#faf9fb] hover:bg-[#faf9fb]/70"
                      >
                        <TableCell className="px-5 py-3">
                          {groupBy === "user" && userNames[group.key] ? (
                            // The gateway groups by user ID; show who that is, and
                            // keep the ID beneath for cross-checking.
                            <div className="min-w-0" title={group.key}>
                              <div className={`${cellClass} truncate`}>
                                {userNames[group.key]}
                              </div>
                              <div className="truncate text-[12px] leading-[16px] text-[#84828e]">
                                {group.key}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`${cellClass} truncate`}
                              title={group.key}
                            >
                              {group.key || "-"}
                            </div>
                          )}
                        </TableCell>
                        <TableCell
                          className={`px-5 py-3 text-right ${cellClass}`}
                        >
                          {integer.format(group.requests || 0)}
                        </TableCell>
                        <TableCell
                          className={`px-5 py-3 text-right ${cellClass}`}
                        >
                          {integer.format(group.tokens?.prompt || 0)}
                        </TableCell>
                        <TableCell
                          className={`px-5 py-3 text-right ${cellClass}`}
                        >
                          {integer.format(group.tokens?.completion || 0)}
                        </TableCell>
                        <TableCell
                          className={`px-5 py-3 text-right ${cellClass}`}
                        >
                          {integer.format(group.tokens?.cached || 0)}
                        </TableCell>
                        <TableCell
                          className={`px-5 py-3 text-right ${cellClass}`}
                        >
                          {integer.format(group.units?.images || 0)}
                        </TableCell>
                        <TableCell
                          className={`px-5 py-3 text-right ${cellClass}`}
                        >
                          {usd(group.costUsd)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CollapsibleSection>
        </div>
      )}
    </section>
  );
}
